import { Accordion as BaseAccordion } from '@base-ui/react/accordion'
import styled, { css } from 'styled-components'
import { accents, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export type AccordionVariant = 'bordered' | 'divided' | 'ghost'
export type AccordionSize = 'sm' | 'md'
export type AccordionIconPosition = 'start' | 'end'

interface StyleProps {
  $variant: AccordionVariant
  $size: AccordionSize
}

const block = { sm: spacing[2], md: spacing[3] } as const
const inline = { sm: spacing[3], md: spacing[4] } as const
const fontSize = {
  sm: typography.text.xs.fontSize,
  md: typography.text.sm.fontSize,
} as const

// Ghost and divided rows run flush with their container.
const insetInline = ({ $variant, $size }: StyleProps): number =>
  $variant === 'bordered' ? inline[$size] : 0

// The instantiation expression keeps Base UI's generic through `styled()`,
// which would otherwise erase it and hand `onValueChange` an `unknown[]`.
export const Root = styled(BaseAccordion.Root<string>)<{
  $variant: AccordionVariant
}>`
  box-sizing: border-box;
  font-family: ${typography.fontFamily};
  color: ${semantic.fg};
  ${({ $variant }) =>
    $variant === 'bordered' &&
    css`
      border: 1px solid ${semantic.border};
      border-radius: ${radii.sm}px;
      background: ${semantic.bg};
      overflow: hidden;
    `}
`

export const Item = styled(BaseAccordion.Item)<{ $variant: AccordionVariant }>`
  ${({ $variant }) =>
    $variant !== 'ghost' &&
    css`
      &:not(:last-child) {
        border-bottom: 1px solid ${semantic.border};
      }
    `}
`

export const Header = styled(BaseAccordion.Header)`
  margin: 0;
  font: inherit;
`

export const Trigger = styled(BaseAccordion.Trigger)<StyleProps>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]}px;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: ${({ $size }) => block[$size]}px ${insetInline}px;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: ${({ $size }) => fontSize[$size]}px;
  font-weight: ${typography.weights.medium};
  text-align: left;
  cursor: pointer;

  ${({ $variant }) =>
    $variant === 'bordered' &&
    css`
      &:hover:not([data-disabled]) {
        background: ${semantic.bgSubtle};
      }
    `}

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: -2px;
  }
`

export const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`

// Trailing summary: never shrinks, so a long label wraps instead.
export const Extra = styled.span`
  flex: 0 0 auto;
  font-weight: ${typography.weights.regular};
`

export const Chevron = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  transform: rotate(-90deg);
  transition: transform ${motion.duration.fast}ms ${motion.ease};

  [data-panel-open] > & {
    transform: rotate(0deg);
  }
`

export const Panel = styled(BaseAccordion.Panel)`
  height: var(--accordion-panel-height);
  transition: height ${motion.duration.base}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    height: 0;
    overflow: hidden;
  }
`

export const Content = styled.div<StyleProps>`
  padding: ${({ $size }) => block[$size]}px ${insetInline}px
    ${({ $size }) => inline[$size]}px;
  font-size: ${({ $size }) => fontSize[$size]}px;
  font-weight: ${typography.weights.regular};
`
